// ABOUTME: Mapping schema for TechLog -> RAAS completed flight transformation.
import { TechlogTruckUnit } from '../../../../types/techLog/fuel';
import { TechlogToRaasFlightInput } from './validation';

const RAAS_FUEL_UNIT_BY_TRUCK_UNIT: Record<
  TechlogTruckUnit,
  'Liters' | 'Gallons'
> = {
  [TechlogTruckUnit.LITER]: 'Liters',
  [TechlogTruckUnit.US_GALLON]: 'Gallons',
};

export const techlogFlightToRaasSchema = {
  serialNumber: (input: TechlogToRaasFlightInput) =>
    input.techlog.aircraft.serialNumber?.trim() || undefined,

  registrationNumber: (input: TechlogToRaasFlightInput) =>
    input.techlog.aircraft.serialNumber?.trim()
      ? undefined
      : input.techlog.aircraft.registration,

  logNumber: (input: TechlogToRaasFlightInput) => {
    const origin = input.techlog.airportDeparture.icaoCode;
    const destination = input.techlog.airportArrival.icaoCode;
    const takeoff = `${new Date(input.techlog.sector.takeoffTime)
      .toISOString()
      .slice(0, 16)}Z`;
    const tripLabel =
      input.techlog.trip.number != null
        ? `#${input.techlog.trip.number}`
        : input.techlog.trip.callsign?.trim()
        ? input.techlog.trip.callsign.trim()
        : input.techlog.trip.srpNumber != null
        ? `SRP-${input.techlog.trip.srpNumber}`
        : input.techlog.sector.id.slice(0, 8);

    return `${tripLabel}/${origin}-${destination}/${takeoff}`;
  },

  flightNumber: (input: TechlogToRaasFlightInput) => {
    if (input.techlog.trip.number != null) {
      return String(input.techlog.trip.number);
    }

    if (input.techlog.trip.callsign?.trim()) {
      return input.techlog.trip.callsign.trim();
    }

    return 'N/A';
  },

  origin: (input: TechlogToRaasFlightInput) =>
    input.techlog.airportDeparture.icaoCode,

  destination: (input: TechlogToRaasFlightInput) =>
    input.techlog.airportArrival.icaoCode,

  liftOffUTC: (input: TechlogToRaasFlightInput) =>
    new Date(input.techlog.sector.takeoffTime).toISOString(),

  touchdownUTC: (input: TechlogToRaasFlightInput) =>
    new Date(input.techlog.sector.landingTime).toISOString(),

  minutesInAir: (input: TechlogToRaasFlightInput) => {
    const takeoffTime = new Date(input.techlog.sector.takeoffTime).getTime();
    const landingTime = new Date(input.techlog.sector.landingTime).getTime();
    const minutesInAir = (landingTime - takeoffTime) / 60000;

    if (minutesInAir <= 0) {
      throw new Error(
        `Sector ${input.techlog.sector.id} has a non-positive flight duration.`,
      );
    }

    return minutesInAir;
  },

  cycles: (input: TechlogToRaasFlightInput) => {
    // TechLog records per-flight values on the sector, so the airframe cycle
    // count is the per-flight increment RAAS expects (not a lifetime total).
    const cycles =
      input.techlog.sector.cycles?.airframe ??
      input.techlog.sector.landingsCount ??
      input.techlog.sector.takeoffsCount ??
      undefined;

    if (cycles == null) {
      throw new Error(
        `Sector ${input.techlog.sector.id} is missing airframe cycles.`,
      );
    }

    return cycles;
  },

  creatingSystemName: () => 'TechLog',

  fuelLift: (input: TechlogToRaasFlightInput) => {
    if (input.techlog.fuelUplifts.length === 0) {
      return undefined;
    }

    const truckUnits = [
      ...new Set(input.techlog.fuelUplifts.map((uplift) => uplift.truckUnit)),
    ];

    if (truckUnits.length !== 1) {
      return undefined;
    }

    const truckUnit = truckUnits[0];
    const unit =
      truckUnit != null ? RAAS_FUEL_UNIT_BY_TRUCK_UNIT[truckUnit] : undefined;

    // Drop the fuel lift rather than guess a unit: an unknown/missing truckUnit
    // would otherwise be sent to RAAS mislabelled.
    if (unit === undefined) {
      return undefined;
    }

    const volume = input.techlog.fuelUplifts.reduce(
      (sum, uplift) => sum + (uplift.truckActualUplift ?? 0),
      0,
    );

    if (volume <= 0) {
      return undefined;
    }

    return {
      volume,
      unit,
    };
  },

  accessories: (input: TechlogToRaasFlightInput) => {
    const supportedAccessories = input.raas.aircraftStatus?.accessorySTT ?? [];

    if (supportedAccessories.length === 0) {
      return undefined;
    }

    // RAAS is the source of truth for which accessories sync: only those with
    // inheritsTime === true, identified by (mapTypeAPI, mapPositionAPI). The
    // ordinal is scoped per type and RAAS-authoritative, so we never interpret
    // the freetext position. mapPositionAPI 0 / empty mapTypeAPI is not API-mappable.
    const enginePositions = [
      ...new Set(
        supportedAccessories
          .filter(
            (accessory) =>
              accessory.inheritsTime === true &&
              ['ENGINE', 'ENG'].includes(
                accessory.mapTypeAPI?.trim().toUpperCase() ?? '',
              ) &&
              accessory.mapPositionAPI != null &&
              accessory.mapPositionAPI > 0,
          )
          .map((accessory) => accessory.mapPositionAPI as number),
      ),
    ].sort((left, right) => left - right);
    const apuPositions = [
      ...new Set(
        supportedAccessories
          .filter(
            (accessory) =>
              accessory.inheritsTime === true &&
              accessory.mapTypeAPI?.trim().toUpperCase() === 'APU' &&
              accessory.mapPositionAPI != null &&
              accessory.mapPositionAPI > 0,
          )
          .map((accessory) => accessory.mapPositionAPI as number),
      ),
    ].sort((left, right) => left - right);
    const takeoffTime = new Date(input.techlog.sector.takeoffTime).getTime();
    const landingTime = new Date(input.techlog.sector.landingTime).getTime();
    const flightMinutes = (landingTime - takeoffTime) / 60000;
    // Engines run during taxi, so block time (offblocks -> onblocks) is a closer
    // proxy for engine running time than flight time. Both are per-flight increments.
    const blockMinutes =
      input.techlog.sector.offblocksTime && input.techlog.sector.onblocksTime
        ? (new Date(input.techlog.sector.onblocksTime).getTime() -
            new Date(input.techlog.sector.offblocksTime).getTime()) /
          60000
        : flightMinutes;
    const airframeCycles =
      input.techlog.sector.cycles?.airframe ??
      input.techlog.sector.landingsCount ??
      input.techlog.sector.takeoffsCount ??
      undefined;
    const accessories: Array<Record<string, unknown>> = [];

    for (const position of enginePositions) {
      const oilUplifts = input.techlog.fluidUplifts.filter(
        (fluid) =>
          fluid.fluidType === 'oil' &&
          fluid.fluidDestination === 'engine' &&
          fluid.fluidDestinationNumber === position,
      );
      const oilUnits = [
        ...new Set(oilUplifts.map((fluid) => fluid.upliftUnit)),
      ];
      const oilVolume = oilUplifts.reduce(
        (sum, fluid) => sum + (fluid.uplift ?? 0),
        0,
      );
      // TechLog records engine data by ordinal (engine_1..4); we attach it to the
      // RAAS engine sharing that ordinal. No shared key (e.g. serial) joins the two
      // systems, so this 1:1 ordinal correspondence is an assumed mapping.
      // Per-flight engine cycles fall back to airframe cycles when absent.
      const engineCycles =
        (
          input.techlog.sector.cycles as
            | Record<string, number | null>
            | undefined
        )?.[`engine_${position}`] ?? airframeCycles;
      accessories.push({
        type: 'Engine',
        position: String(position),
        minutes: blockMinutes > 0 ? blockMinutes : flightMinutes,
        cycles: engineCycles,
        fluid:
          oilVolume > 0 && oilUnits.length === 1
            ? {
                volume: oilUnits[0] === 'ml' ? oilVolume / 1000 : oilVolume,
                unit: oilUnits[0] === 'ml' ? 'Liters' : 'Quarts',
              }
            : undefined,
      });
    }

    const apuUplifts = input.techlog.fluidUplifts.filter(
      (fluid) => fluid.fluidType === 'oil' && fluid.fluidDestination === 'apu',
    );
    const apuUnits = [...new Set(apuUplifts.map((fluid) => fluid.upliftUnit))];
    const apuVolume = apuUplifts.reduce(
      (sum, fluid) => sum + (fluid.uplift ?? 0),
      0,
    );
    const apuMinutes =
      input.techlog.sector.apuSeconds != null
        ? input.techlog.sector.apuSeconds / 60
        : undefined;
    // Per-flight APU cycles from the sector; do not invent a value when absent.
    const apuCycles = input.techlog.sector.cycles?.apu ?? undefined;
    const apuHasActivity =
      (apuMinutes ?? 0) > 0 || (apuCycles ?? 0) > 0 || apuVolume > 0;

    // TechLog tracks a single APU; attach it to each RAAS-tracked APU position.
    for (const position of apuPositions) {
      if (!apuHasActivity) {
        break;
      }

      accessories.push({
        type: 'APU',
        position: String(position),
        minutes: apuMinutes,
        cycles: apuCycles,
        fluid:
          apuVolume > 0 && apuUnits.length === 1
            ? {
                volume: apuUnits[0] === 'ml' ? apuVolume / 1000 : apuVolume,
                unit: apuUnits[0] === 'ml' ? 'Liters' : 'Quarts',
              }
            : undefined,
      });
    }

    return accessories.length > 0 ? accessories : undefined;
  },
};
